import { useState, memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Activity, Users, Calendar, MapPin, Award, Check, Clock,
  TrendingUp, Bell, MessageCircle, FileText,
  Pill, Home, Target, Star
} from 'lucide-react';
import { GovLayout, GovPageTitle, GovCard, GovButton, GovEmptyState } from '@/components/layout/GovLayout';

// ================================================================================
// STAT CARD - Fast memoized component
// ================================================================================
interface StatCardProps {
  icon: React.ElementType<{ className?: string }>;
  label: string;
  value: string | number;
  change?: string;
  color: string;
}

const StatCard = memo(({ icon: Icon, label, value, change, color }: StatCardProps) => (
  <GovCard className="hover:shadow-md transition-shadow">
    <div className="flex items-center gap-4">
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${color}`}>
        <Icon className="w-6 h-6 text-white" />
      </div>
      <div>
        <div className="text-2xl font-bold text-gray-900">{value}</div>
        <div className="text-sm text-gray-500">{label}</div>
        {change && (
          <div className="text-xs text-green-600 flex items-center gap-1 mt-1">
            <TrendingUp className="w-3 h-3" />
            {change}
          </div>
        )}
      </div>
    </div>
  </GovCard>
));

StatCard.displayName = 'StatCard';

// ================================================================================
// PATIENT FOLLOW-UP CARD - Memoized for performance
// ================================================================================
interface Patient {
  name: string;
  age: number;
  phase: number;
  nextVisit: string;
  status: 'on-track' | 'attention' | 'missed';
  phone: string;
  address: string;
}

interface PatientFollowUpCardProps {
  patient: Patient;
  onMessage?: () => void;
  onVisit?: () => void;
}

const PatientFollowUpCard = memo(({ patient, onMessage, onVisit }: PatientFollowUpCardProps) => {
  const statusConfig = {
    'on-track': { color: 'text-green-600', bg: 'bg-green-50', label: 'On Track' },
    'attention': { color: 'text-amber-600', bg: 'bg-amber-50', label: 'Needs Attention' },
    'missed': { color: 'text-red-600', bg: 'bg-red-50', label: 'Missed Visit' }
  };

  const config = statusConfig[patient.status];

  return (
    <GovCard>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-semibold">
            {patient.name.charAt(0)}
          </div>
          <div>
            <h4 className="font-medium text-gray-900">{patient.name}</h4>
            <p className="text-sm text-gray-500">{patient.age} yrs • Phase {patient.phase}</p>
          </div>
        </div>
        <span className={`text-xs px-2 py-1 rounded-full ${config.bg} ${config.color}`}>{config.label}</span>
      </div>
      <div className="space-y-2 text-sm text-gray-600">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4" />
          <span>Next Visit: {patient.nextVisit}</span>
        </div>
        <div className="flex items-center gap-2">
          <MapPin className="w-4 h-4" />
          <span className="truncate">{patient.address}</span>
        </div>
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4" />
          <span>{patient.phone}</span>
        </div>
      </div>
      <div className="flex gap-2 mt-4">
        <GovButton variant="secondary" size="sm" className="flex-1" onClick={onMessage}>
          <MessageCircle className="w-4 h-4 mr-1" />
          Message
        </GovButton>
        <GovButton variant="primary" size="sm" className="flex-1" onClick={onVisit}>
          <Home className="w-4 h-4 mr-1" />
          Visit
        </GovButton>
      </div>
    </GovCard>
  );
});

PatientFollowUpCard.displayName = 'PatientFollowUpCard';

// ================================================================================
// INCENTIVE CARD - Simple memoized component
// ================================================================================
interface Incentive {
  month: string;
  patientsVisited: number;
  amount: number;
  status: 'pending' | 'approved' | 'received';
}

const IncentiveCard = memo(({ month, patientsVisited, amount, status }: Incentive) => {
  const statusConfig = {
    pending: { color: 'text-amber-600', bg: 'bg-amber-50', label: 'Pending' },
    approved: { color: 'text-blue-600', bg: 'bg-blue-50', label: 'Approved' },
    received: { color: 'text-green-600', bg: 'bg-green-50', label: 'Received' }
  };

  const config = statusConfig[status];

  return (
    <div className="flex items-center justify-between p-4 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors">
      <div>
        <h4 className="font-medium text-gray-900">{month}</h4>
        <p className="text-sm text-gray-500">{patientsVisited} patients visited</p>
      </div>
      <div className="text-right">
        <div className="text-lg font-bold text-green-600">₹{amount.toLocaleString()}</div>
        <span className={`text-xs px-2 py-0.5 rounded-full ${config.bg} ${config.color}`}>{config.label}</span>
      </div>
    </div>
  );
});

IncentiveCard.displayName = 'IncentiveCard';

// ================================================================================
// ACHIEVEMENT BADGE - Memoized component
// ================================================================================
interface AchievementBadgeProps {
  icon: React.ElementType<{ className?: string }>;
  title: string;
  description: string;
  unlocked: boolean;
}

const AchievementBadge = memo(({ icon: Icon, title, description, unlocked }: AchievementBadgeProps) => (
  <div className={`flex flex-col items-center p-3 rounded-xl ${unlocked ? 'bg-amber-50' : 'bg-gray-50 opacity-50'}`}>
    <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 ${unlocked ? 'bg-gradient-to-br from-amber-400 to-amber-600' : 'bg-gray-300'}`}>
      <Icon className={`w-5 h-5 ${unlocked ? 'text-white' : 'text-gray-500'}`} />
    </div>
    <h4 className={`font-medium text-xs text-center ${unlocked ? 'text-gray-900' : 'text-gray-500'}`}>{title}</h4>
    <p className="text-xs text-gray-400 text-center mt-0.5">{description}</p>
    {unlocked && <Star className="w-3 h-3 text-amber-500 mt-1" />}
  </div>
));

AchievementBadge.displayName = 'AchievementBadge';

// ================================================================================
// MAIN NIKSHAY MITRA DASHBOARD
// ================================================================================
const NikshayMitraDashboard = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'patients' | 'schedule' | 'incentives'>('patients');

  // Memoized stats data
  const stats = [
    { icon: Users, label: 'Assigned Patients', value: 25, change: '+2 this week', color: 'bg-gradient-to-br from-blue-500 to-blue-600' },
    { icon: Check, label: 'Completed Visits', value: 18, change: '90%', color: 'bg-gradient-to-br from-green-500 to-green-600' },
    { icon: Clock, label: 'Pending Visits', value: 7, color: 'bg-gradient-to-br from-amber-500 to-amber-600' },
    { icon: Award, label: 'Incentive Earned', value: '₹8,500', color: 'bg-gradient-to-br from-cyan-500 to-cyan-600' }
  ];

  const patients: Patient[] = [
    { name: 'Suresh Yadav', age: 42, phase: 2, nextVisit: 'Tomorrow', status: 'on-track', phone: '+91 98765 43210', address: 'Ward 5, Block A' },
    { name: 'Kavita Devi', age: 35, phase: 4, nextVisit: '15 Jan', status: 'attention', phone: '+91 87654 32109', address: 'Ward 3, Block B' },
    { name: 'Mohan Lal', age: 50, phase: 1, nextVisit: 'Missed', status: 'missed', phone: '+91 76543 21098', address: 'Ward 7, Block C' },
    { name: 'Anita Sharma', age: 28, phase: 3, nextVisit: '18 Jan', status: 'on-track', phone: '+91 65432 10987', address: 'Ward 2, Block A' }
  ];

  const schedule = [
    { time: '09:00 AM', patient: 'Suresh Yadav', type: 'Follow-up', address: 'Ward 5' },
    { time: '10:30 AM', patient: 'Kavita Devi', type: 'Medicine Distribution', address: 'Ward 3' },
    { time: '12:00 PM', patient: 'Mohan Lal', type: 'Missed Visit Follow-up', address: 'Ward 7' },
    { time: '02:00 PM', patient: 'Anita Sharma', type: 'Phase 3 Review', address: 'Ward 2' },
    { time: '03:30 PM', patient: 'Rajesh Kumar', type: 'New Patient Enrollment', address: 'Ward 1' }
  ];

  const incentives: Incentive[] = [
    { month: 'December 2025', patientsVisited: 22, amount: 7500, status: 'received' },
    { month: 'November 2025', patientsVisited: 24, amount: 8000, status: 'received' },
    { month: 'October 2025', patientsVisited: 20, amount: 6800, status: 'received' },
    { month: 'January 2026', patientsVisited: 18, amount: 6200, status: 'pending' }
  ];

  const achievements = [
    { icon: Target, title: '100 Visits', description: 'Complete 100 home visits', unlocked: true },
    { icon: Star, title: 'Top Performer', description: 'Best TB Supporter of the month', unlocked: true },
    { icon: Award, title: 'Zero Default', description: 'No missed visits in 3 months', unlocked: false },
    { icon: Users, title: 'Community Hero', description: 'Enroll 50+ patients', unlocked: true }
  ];

  // Tab change handler - memoized
  const handleTabChange = useCallback((tab: 'patients' | 'schedule' | 'incentives') => {
    setActiveTab(tab);
  }, []);

  const tabs = [
    { id: 'patients' as const, label: 'Patient Follow-ups', icon: Users },
    { id: 'schedule' as const, label: 'Visit Schedule', icon: Calendar },
    { id: 'incentives' as const, label: 'Incentives', icon: Award }
  ];

  return (
    <GovLayout showSidebar={false}>
      <div className="space-y-6">
        {/* Page Title */}
        <GovPageTitle
          title={t('dashboard.nikshayMitra.title')}
          subtitle={`You have ${stats[2].value} visits pending today`}
          icon={Activity}
        />

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map((stat, index) => (
            <StatCard key={index} {...stat} />
          ))}
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left Column - 2 cols */}
          <div className="lg:col-span-2 space-y-6">
            {/* Tab Navigation Card */}
            <GovCard>
              {/* Tab Headers */}
              <div className="flex border-b border-gray-200 -mx-4 -mt-4 px-4 pt-4">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => handleTabChange(tab.id)}
                      className={`flex-1 px-4 py-3 font-medium transition-colors flex items-center justify-center gap-2 ${
                        isActive
                          ? 'text-blue-600 border-b-2 border-blue-600'
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      <span className="hidden sm:inline">{tab.label}</span>
                    </button>
                  );
                })}
              </div>

              {/* Tab Content */}
              <div className="mt-4">
                {/* Patients Tab */}
                {activeTab === 'patients' && (
                  <div className="space-y-4">
                    {patients.length > 0 ? (
                      patients.map((patient, index) => (
                        <PatientFollowUpCard key={index} patient={patient} />
                      ))
                    ) : (
                      <GovEmptyState
                        icon="patients"
                        title="No Pending Follow-ups"
                        description="All patients are up to date with their treatment"
                      />
                    )}
                  </div>
                )}

                {/* Schedule Tab */}
                {activeTab === 'schedule' && (
                  <div className="space-y-3">
                    {schedule.map((item, index) => (
                      <div key={index} className="flex items-center gap-4 p-4 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors">
                        <div className="w-20 text-center">
                          <div className="text-lg font-semibold text-blue-600">{item.time}</div>
                        </div>
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900">{item.patient}</h4>
                          <p className="text-sm text-gray-500">{item.type} • {item.address}</p>
                        </div>
                        <div className="flex gap-2">
                          <button className="p-2 hover:bg-gray-200 rounded-full transition-colors">
                            <MapPin className="w-4 h-4 text-gray-500" />
                          </button>
                          <button className="p-2 hover:bg-gray-200 rounded-full transition-colors">
                            <MessageCircle className="w-4 h-4 text-gray-500" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Incentives Tab */}
                {activeTab === 'incentives' && (
                  <div className="space-y-4">
                    {/* Total Summary */}
                    <div className="p-4 rounded-xl bg-gradient-to-r from-green-50 to-transparent border border-green-100">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-500">Total Earned (This Year)</p>
                          <p className="text-3xl font-bold text-green-600">₹28,500</p>
                        </div>
                        <div className="w-14 h-14 rounded-full bg-green-500 flex items-center justify-center">
                          <Award className="w-7 h-7 text-white" />
                        </div>
                      </div>
                    </div>
                    {incentives.map((incentive, index) => (
                      <IncentiveCard key={index} {...incentive} />
                    ))}
                  </div>
                )}
              </div>
            </GovCard>

            {/* Medicine Distribution Card */}
            <GovCard>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Medicine Distribution Today</h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-4 rounded-xl bg-green-50">
                  <Pill className="w-6 h-6 mx-auto mb-2 text-green-600" />
                  <div className="text-2xl font-bold text-green-600">15</div>
                  <div className="text-sm text-gray-500">Distributed</div>
                </div>
                <div className="text-center p-4 rounded-xl bg-amber-50">
                  <Clock className="w-6 h-6 mx-auto mb-2 text-amber-600" />
                  <div className="text-2xl font-bold text-amber-600">5</div>
                  <div className="text-sm text-gray-500">Pending</div>
                </div>
                <div className="text-center p-4 rounded-xl bg-blue-50">
                  <FileText className="w-6 h-6 mx-auto mb-2 text-blue-600" />
                  <div className="text-2xl font-bold text-blue-600">20</div>
                  <div className="text-sm text-gray-500">Total</div>
                </div>
              </div>
            </GovCard>
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* Achievements Card */}
            <GovCard>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Achievements</h3>
              <div className="grid grid-cols-2 gap-3">
                {achievements.map((achievement, index) => (
                  <AchievementBadge key={index} {...achievement} />
                ))}
              </div>
            </GovCard>

            {/* Quick Actions Card */}
            <GovCard>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
              <div className="space-y-2">
                <GovButton variant="secondary" className="w-full justify-start">
                  <Users className="w-4 h-4 mr-2" />
                  Add New Patient
                </GovButton>
                <GovButton variant="secondary" className="w-full justify-start">
                  <FileText className="w-4 h-4 mr-2" />
                  Submit Report
                </GovButton>
                <GovButton variant="secondary" className="w-full justify-start">
                  <Pill className="w-4 h-4 mr-2" />
                  Update Medicine Stock
                </GovButton>
                <GovButton variant="secondary" className="w-full justify-start">
                  <Calendar className="w-4 h-4 mr-2" />
                  Reschedule Visit
                </GovButton>
              </div>
            </GovCard>

            {/* Training Progress Card */}
            <GovCard>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Training Progress</h3>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-700">TB Basics</span>
                    <span className="text-green-600 font-medium">100%</span>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div className="h-full bg-green-500 w-full" />
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-700">Patient Communication</span>
                    <span className="text-green-600 font-medium">100%</span>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div className="h-full bg-green-500 w-full" />
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-700">Medicine Management</span>
                    <span className="text-blue-600 font-medium">75%</span>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div className="h-full bg-blue-500 w-3/4" />
                  </div>
                </div>
              </div>
              <GovButton variant="primary" className="w-full mt-4">
                Continue Training
              </GovButton>
            </GovCard>
          </div>
        </div>
      </div>
    </GovLayout>
  );
};

export default memo(NikshayMitraDashboard);